import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, Calendar, User, Download, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InsightPreviewPage() {
  const { id } = useParams();
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/insights/preview/${id}`).then(res => {
      setInsight(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const previewTitle = insight ? ("Preview: " + insight.title + " | Equaliser Legal LLP") : "Preview | Equaliser Legal LLP";
  useSEO(previewTitle); // eslint-disable-line react-hooks/rules-of-hooks

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-20"><p className="text-slate-400">Loading...</p></div>;
  if (!insight) return <div className="min-h-screen flex flex-col items-center justify-center pt-20"><p className="text-slate-400 mb-4">Preview not available.</p><Link to="/insights" className="text-[#C5A059] hover:underline text-sm">Back to Insights</Link></div>;

  return (
    <div data-testid="insight-preview-page">
      <section className="bg-[#0A1628] noise-overlay relative pt-28 pb-16 md:pt-36 md:pb-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-amber-500/20 border border-amber-500/30 px-4 py-2 rounded-[2px] mb-6 flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-400" />
            <span className="text-amber-300 text-xs uppercase tracking-wider">Preview Mode — Not publicly visible</span>
          </div>
          <Link to="/admin/insights" className="inline-flex items-center text-white/50 hover:text-[#C5A059] transition-colors text-sm mb-8"><ArrowLeft size={14} className="mr-1" /> Back to Admin</Link>
          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs uppercase tracking-wider text-[#C5A059]">{insight.category}</span>
            <span className="text-white/30">|</span>
            <span className="flex items-center gap-1 text-white/40 text-xs"><Calendar size={12} /> {insight.published_date}</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-medium leading-tight tracking-tight">{insight.title}</h1>
          <div className="flex items-center gap-4 mt-6"><span className="flex items-center gap-1.5 text-white/50 text-sm"><User size={14} /> {insight.author}</span></div>
        </div>
      </section>
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-2 mb-10">{(insight.tags || []).map((tag, i) => <span key={i} className="text-xs border border-slate-200 text-slate-500 px-3 py-1 rounded-[2px]">{tag}</span>)}</div>
          {insight.excerpt && <p className="text-slate-600 text-lg leading-relaxed mb-10 border-l-2 border-[#C5A059] pl-6 italic">{insight.excerpt}</p>}
          <div className="insight-content" dangerouslySetInnerHTML={{ __html: insight.content }} />
          {insight.documents && insight.documents.length > 0 && (
            <div className="mt-10 border-t border-slate-200 pt-6">
              <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-4">Downloads</h3>
              {insight.documents.map((doc, i) => (
                <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#0A1628] text-[#0A1628] px-4 py-2 text-sm hover:bg-[#0A1628] hover:text-white transition-colors rounded-[2px] mr-3 mb-2"><Download size={14} /> {doc.name}</a>
              ))}
            </div>
          )}
          <div className="mt-16 border-t border-slate-200 pt-8"><p className="text-slate-400 text-xs leading-relaxed italic">This material is for general information only and does not constitute legal advice or a solicitation.</p></div>
        </div>
      </section>
    </div>
  );
}
